import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  formatCurrency,
  formatDatePl,
} from "@/features/billing/billing-format";
import type {
  BillingAccountResponse,
  BillingPlansResponse,
  PlanInterval,
} from "@/types";
import { SubscriptionPlanCard } from "./SubscriptionPlanCard";

interface SubscriptionBillingViewProps {
  account: BillingAccountResponse | null;
  checkoutLoading: string | null;
  onManageSubscription: () => void;
  onSelectInterval: (interval: PlanInterval) => void;
  onSubscribe: (productId: string, priceId: string, planName?: string) => void;
  plans: BillingPlansResponse | null;
  portalLoading: boolean;
  selectedInterval: PlanInterval;
  showMetered: boolean;
  showSubscriptions: boolean;
}

export function SubscriptionBillingView({
  account,
  checkoutLoading,
  onManageSubscription,
  onSelectInterval,
  onSubscribe,
  plans,
  portalLoading,
  selectedInterval,
  showMetered,
  showSubscriptions,
}: SubscriptionBillingViewProps) {
  const { t } = useTranslation(["billing"]);
  const hasSubscription = account?.subscription?.status === "active" ||
    account?.subscription?.status === "trialing";
  const hasAccount = account?.hasAccount ?? false;

  return (
    <>
      {account
        ? (
          <div className="rounded-lg border border-border bg-card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">
                {t("billing:status.currentPlan")}
              </span>
              {hasSubscription
                ? (
                  <span className="inline-flex items-center rounded-full bg-[color:color-mix(in_srgb,var(--risk-low)_16%,var(--card))] px-2.5 py-0.5 text-xs font-medium text-success">
                    {account.subscription?.plan?.productName ||
                      t("billing:status.active")}
                  </span>
                )
                : (
                  <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground">
                    {hasAccount
                      ? t("billing:status.noSubscription")
                      : t("billing:status.noBillingAccount")}
                  </span>
                )}
            </div>

            <div className="pt-2 border-t border-border">
              {account.billingMode && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {t("billing:status.billingMode")}
                  </span>
                  <span className="font-medium text-foreground">
                    {account.billingMode}
                  </span>
                </div>
              )}
              {hasSubscription && account.subscription?.periodEndsAt && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {t("billing:status.currentPeriodEnds")}:{" "}
                  {formatDatePl(account.subscription.periodEndsAt)}
                </p>
              )}
            </div>

            {hasSubscription && account.subscription?.plan && (
              <div className="pt-2 border-t border-border space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("billing:status.billing")}
                  </span>
                  <span
                    className="text-foreground"
                    data-test="subscription-amount"
                  >
                    {formatCurrency(
                      account.subscription.plan.amount,
                      account.subscription.currency.toUpperCase(),
                    )} / {account.subscription.plan.interval}
                  </span>
                </div>
                {account.subscription.cancelAtPeriodEnd && (
                  <div className="rounded-md bg-[color:color-mix(in_srgb,var(--risk-medium)_18%,var(--card))] px-3 py-2 text-warning">
                    {t("billing:status.cancelsAtPeriodEnd")}
                    {account.subscription.periodEndsAt
                      ? ` (${formatDatePl(account.subscription.periodEndsAt)})`
                      : ""}
                  </div>
                )}
              </div>
            )}

            {hasSubscription && (
              <Button
                variant="outline"
                onClick={onManageSubscription}
                disabled={portalLoading}
                className="w-full"
                data-test="billing-manage-subscription"
              >
                {portalLoading
                  ? t("billing:status.opening")
                  : t("billing:status.manageSubscription")}
              </Button>
            )}
          </div>
        )
        : (
          <div className="rounded-lg border border-[color:color-mix(in_srgb,var(--risk-medium)_35%,transparent)] bg-[color:color-mix(in_srgb,var(--risk-medium)_18%,var(--card))] p-4">
            <p className="text-sm text-warning">
              {t("billing:status.notAvailable")}
            </p>
          </div>
        )}

      {showSubscriptions && plans && (!hasSubscription || !account) && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-md font-medium text-foreground">
              {t("billing:plans.availablePlans")}
            </h3>
            <div className="inline-flex rounded-lg border border-border p-1">
              <Button
                variant={selectedInterval === "month" ? "default" : "ghost"}
                size="sm"
                onClick={() => onSelectInterval("month")}
                data-test="billing-interval-month"
              >
                {t("billing:plans.monthly")}
              </Button>
              <Button
                variant={selectedInterval === "year" ? "default" : "ghost"}
                size="sm"
                onClick={() => onSelectInterval("year")}
                data-test="billing-interval-year"
              >
                {t("billing:plans.yearly")}
              </Button>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {plans.subscriptions.map((plan) => (
              <SubscriptionPlanCard
                key={plan.id}
                interval={selectedInterval}
                loading={checkoutLoading}
                onSubscribe={onSubscribe}
                plan={plan}
              />
            ))}
          </div>
        </div>
      )}

      {/* One-time credit packs moved to PrepaidBillingView (pirx-spma2.4): the
          credit shop is owned by the prepaid view so it renders in credit-only
          mode without the subscription surface. */}

      {showMetered && !showSubscriptions && !hasSubscription && (
        <div className="rounded-lg border border-[color:color-mix(in_srgb,var(--info)_35%,transparent)] bg-[color:color-mix(in_srgb,var(--info)_16%,var(--card))] p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[color:color-mix(in_srgb,var(--info)_18%,var(--card))]">
              <svg
                className="h-5 w-5 text-info"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-info">
                {t("billing:metered.title")}
              </h4>
              <p className="text-sm text-info">
                {t("billing:metered.description")}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
